import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { ToastManager } from '@porsche-design-system/components-angular';
import { catchError, throwError } from 'rxjs';
import { COMMON_MESSAGES } from '../constants/app.constants';
import { AuthUserService } from '../services/auth-user.service';
import { LoggingService } from '../services/logging.service';

/**
 * Functional HTTP interceptor for authentication.
 *
 * - Adds the `Authorization: Bearer <token>` header when a session exists.
 * - On 401/403 shows a toast, clears the session and redirects to the authorization route.
 *
 * @param req Outgoing request.
 * @param next Next handler in the chain.
 * @returns The response stream, with auth errors handled and re-thrown.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthUserService);
  const toast = inject(ToastManager);
  const logger = inject(LoggingService);

  // Attach the token only when present (avoids cloning the request needlessly)
  const token = auth.getToken();
  const authReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 || error.status === 403) {
        logger.warn(`HTTP ${error.status}`, { url: req.url });
        toast.addMessage({
          text:
            error.status === 401 ? COMMON_MESSAGES['401_MESSAGE'] : COMMON_MESSAGES['403_MESSAGE'],
          state: 'info',
        });
        auth.signOut();
      } else {
        logger.error('HTTP error', error, { url: req.url, status: error.status });
      }
      // Re-throw so callers can still react to the failure
      return throwError(() => error);
    }),
  );
};
